package dev.lingvo.ops.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.ops.api.OpsApi
import dev.lingvo.ops.components.OpsEmptyState
import dev.lingvo.ops.components.OpsLoad
import dev.lingvo.ops.components.OpsPageHeader
import dev.lingvo.ops.components.OpsPanel
import dev.lingvo.ops.components.OpsPlatformGridStyle
import dev.lingvo.ops.components.OpsResourceStates
import dev.lingvo.ops.components.OpsShell
import dev.lingvo.ops.state.OpsAttentionItem
import dev.lingvo.ops.state.OpsResourceState
import dev.lingvo.ops.state.attentionItems
import dev.lingvo.ops.state.overviewStatus
import dev.lingvo.ops.state.rememberOpsResource
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatCount
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Page
@Composable
fun IndexPage() {
    OpsShell("Overview") {
        // Independent effects begin together. One slow/refused check cannot hold the others back.
        val jobs = rememberOpsResource(Unit) { OpsApi.jobHealth() }
        val webhooks = rememberOpsResource(Unit) { OpsApi.webhookHealth() }
        val checkouts = rememberOpsResource(Unit) { OpsApi.checkoutHealth() }
        val seats = rememberOpsResource(Unit) { OpsApi.seatHealth() }
        val keys = rememberOpsResource(Unit) { OpsApi.providerHealth() }
        val status = overviewStatus(jobs.state, webhooks.state, checkouts.state, seats.state, keys.state)
        fun refresh() { jobs.refresh(); webhooks.refresh(); checkouts.refresh(); seats.refresh(); keys.refresh() }
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            OpsPageHeader("Overview", "Start with exceptions, then open the record behind each finding.", status.refreshing, ::refresh)
            OpsPanel("Needs attention", "Five independent, bounded checks. This is a diagnostic view, not a guarantee of overall service health.") {
                if (status.canConfirmFindings) {
                    val findings = attentionItems(
                        jobs.state.value(), webhooks.state.value(), checkouts.state.value(),
                        seats.state.value(), keys.state.value(),
                    )
                    if (findings.isEmpty()) {
                        OpsEmptyState("No findings in these five checks", "Each check answered successfully. Other service conditions may still need review.")
                    } else findings.forEach { AttentionRow(it) }
                } else {
                    LAlert(
                        title = when {
                            status.failed > 0 -> "Health summary is incomplete"
                            status.refreshing -> "Refreshing health checks"
                            else -> "Checking service health"
                        },
                        description = "${status.completed} of ${status.total} checks have a result. " +
                            if (status.failed > 0) "${status.failed} check(s) could not refresh. Review or retry the panels below; previous results may be stale."
                            else "Wait for every check before treating this summary as current.",
                        variant = if (status.failed > 0) LAlertVariant.WARNING else LAlertVariant.INFO,
                    )
                }
            }
            Column(OpsPlatformGridStyle.toModifier()) {
                HealthPanel("Jobs", jobs.state, "/jobs", jobs::refresh) { "${formatCount(it.recent.size)} jobs in the health window" }
                HealthPanel("Webhooks", webhooks.state, "/webhooks", webhooks::refresh) { "${formatCount(it.rows.size)} endpoints in this health window" }
                HealthPanel("Checkouts", checkouts.state, "/workspaces", checkouts::refresh) { "${formatCount(it.rows.size)} unsettled checkouts in this window" }
                HealthPanel("Seats", seats.state, "/workspaces", seats::refresh) { "${formatCount(it.rows.size)} workspace seat findings" }
                HealthPanel("Provider keys", keys.state, "/platform", keys::refresh) { "Sentinel: ${it.sentinel.name}" }
            }
        }
    }
}

@Composable
private fun AttentionRow(item: OpsAttentionItem) {
    Div {
        Span(Modifier.fontWeight(700).toAttrs()) { Text(item.title) }
        P { Text(item.detail); Text(" · "); Link(item.href) { Text("Review finding") } }
    }
}

@Composable
private fun <T> HealthPanel(title: String, state: OpsResourceState<T>, href: String, retry: () -> Unit, summary: (T) -> String) {
    OpsPanel(title) {
        OpsResourceStates(state, "No findings in this window", "$title returned no recorded findings in its bounded window.", retry = retry) { value ->
            P { Text(summary(value)); Text(" · "); Link(href) { Text("Open $title") } }
        }
    }
}

private fun <T> OpsResourceState<T>.value(): T = (load as OpsLoad.Ready<T>).value
